import {
  inventoryTransferMaxQuantity,
  inventoryTransferReceivingLocations,
  inventoryTransferViewCounts,
  inventoryTransferViewRecords,
  type InventoryTransferView,
} from "./inventory-transfer";
import { orderListItemSize } from "./order-list";
import { defaultInventorySourceLocation, defaultInventoryReplenishmentLocation, type InventorySourceLocation } from "./inventory-source-location";
import type { InventoryRelocationRegroupPlan } from "./inventory-relocation-group";

export interface InventoryTransferRecord {
  id: string;
  orderId: string;
  legacyId: string;
  orderName: string;
  customerName: string;
  lineItemId: string;
  sku: string;
  size: string;
  specificName: string;
  requestLocation: string;
  receivingLocation: string;
  quantity: number;
  requestedAt: string;
  processingStatus: "未依頼" | "未確認" | "確認済み" | "出荷済み";
  processingAt: string;
  confirmed: boolean;
  confirmedAt: string;
}

export interface InventoryReplenishmentRecord {
  id: string;
  batchId: string;
  productId: string;
  variantId: string;
  productTitle: string;
  productImage: { url: string; altText: string | null } | null;
  variantTitle: string;
  sku: string;
  barcodeValue: string;
  replenishmentIssue: string;
  quantity: number;
  selectedLocationId: string;
  selectedLocationName: string;
  processingStatus: "未依頼" | "依頼中" | "保留中" | "保留・再依頼要" | "受付・在庫確認中";
  requestedByEmail: string;
  requestedAt: string;
  requestType?: string;
  desiredShippingDate?: string;
  visitAt?: string;
  customerName?: string;
  remarks?: string;
}

export interface InventoryTransferData {
  records: InventoryTransferRecord[];
  replenishmentRecords?: InventoryReplenishmentRecord[];
  replenishmentCount?: number;
  relocationRecords?: InventoryRelocationRecord[];
  relocationLocations?: InventorySourceLocation[];
  relocationLocationsError?: string;
}

export interface InventoryRelocationRecord {
  id: string;
  revision: number;
  variantId?: string;
  shopifyTransferId?: string;
  shopifyTransferName?: string;
  shopifyRegisteredAt?: string;
  shopifySyncOperationId?: string;
  productImage: { url: string; altText: string | null } | null;
  replenishmentIssue: string;
  barcodeValue: string;
  requestType: string;
  visitAt: string;
  customerName: string;
  groupName: string;
  groupDate: string;
  requestedAt: string;
  requestedByEmail: string;
  productTitle: string;
  variantTitle: string;
  sku: string;
  quantity: number;
  desiredShippingDate: string;
  processingStatus: string;
  sourceLocationId?: string;
  sourceLocationName?: string;
  replenishmentLocationId?: string;
  replenishmentLocationName?: string;
}

export function inventoryRelocationSelection(records: readonly InventoryReplenishmentRecord[]) {
  const isEligible = (record: InventoryReplenishmentRecord) =>
    ["依頼中", "保留中", "保留・再依頼要", "受付・在庫確認中"].includes(record.processingStatus);
  return {
    eligible: records.filter(isEligible),
    excluded: records.filter((record) => !isEligible(record)),
  };
}

export function selectedInventoryRelocationRecords(
  records: readonly InventoryRelocationRecord[], selectedGroups: ReadonlySet<string>,
): InventoryRelocationRecord[] {
  return records.filter((record) => selectedGroups.has(record.groupName));
}

export function inventoryRelocationConfirmationHtml(records: readonly InventoryReplenishmentRecord[]): string {
  const { eligible, excluded } = inventoryRelocationSelection(records);
  const section = (items: readonly InventoryReplenishmentRecord[], title: string, excludedSection = false) =>
    `<section class="inventory-relocation-confirm-section${excludedSection ? " is-excluded" : ""}"><h3>${title}（${items.length}件）</h3>
      <table><thead><tr><th>SKU</th><th>数量</th>${excludedSection ? "<th>処理状況</th>" : ""}</tr></thead>
      <tbody>${items.map((record) => `<tr><td><code>${escapeHtml(record.sku || "—")}</code></td><td>${record.quantity}</td>${excludedSection ? `<td>${escapeHtml(record.processingStatus)}</td>` : ""}</tr>`).join("") || `<tr><td colspan="${excludedSection ? 3 : 2}">該当する商品はありません。</td></tr>`}</tbody></table></section>`;
  return `<form class="inventory-transfer-request-form" data-relocation-form>
    <header class="inventory-transfer-request-head"><div><p>INVENTORY TRANSFER</p><h2 id="inventory-relocation-title">移管内容の確認</h2></div><button class="dialog-close" type="button" data-relocation-close aria-label="閉じる">×</button></header>
    <div class="inventory-transfer-request-body"><p class="inventory-transfer-request-guide">在庫移管から、印刷及び在庫移管処理を行ってください。</p>
      <div class="inventory-relocation-progress" data-relocation-progress role="status" aria-live="polite"><span class="inventory-registration-spinner" aria-hidden="true"></span><div><strong>在庫移管へ移管中…</strong><small>処理が完了するまで、そのままお待ちください。</small></div></div>
      ${section(eligible, "移管対象SKU")}${excluded.length ? section(excluded, "対象外SKU", true) : ""}
    </div>
    <footer class="inventory-transfer-request-actions"><span>${eligible.length}件を移管</span><div class="inventory-relocation-confirm-actions"><button class="button secondary" type="button" data-relocation-close>取消</button><button class="button inventory-relocation-submit" type="submit" ${eligible.length === 0 ? "disabled" : ""}>移管する</button></div></footer>
  </form>`;
}

export function inventoryRelocationRegroupConfirmationHtml(plan: InventoryRelocationRegroupPlan): string {
  const locationName = (value: string) => escapeHtml(value || "未選択");
  return `<div class="inventory-transfer-request-form">
    <header class="inventory-transfer-request-head"><div><p>INVENTORY TRANSFER</p><h2 id="inventory-relocation-title">⚠ グループ変更の確認</h2></div><button class="dialog-close" type="button" data-regroup-cancel aria-label="閉じる">×</button></header>
    <div class="inventory-transfer-request-body">
      <p class="inventory-transfer-request-guide" role="alert">1つのグループには、在庫元・補充元をそれぞれ1ヶ所のみ登録できます。
      ${plan.targetGroupName ? `この商品を既存グループ「${escapeHtml(plan.targetGroupName)}」へ移動します。` : "変更すると新しいPST番号を自動発行し、この商品を別グループへ移動します。"}</p>
      <section class="inventory-relocation-confirm-section is-excluded"><h3>${escapeHtml(plan.sku)} ／ ${plan.quantity}点</h3>
        <table><thead><tr><th>項目</th><th>変更前</th><th>変更後</th></tr></thead><tbody>
          <tr><th>グループ名</th><td>${escapeHtml(plan.fromGroupName)}</td><td>${plan.targetGroupName ? escapeHtml(plan.targetGroupName) : "新しいPST番号（確定時に発行）"}</td></tr>
          <tr><th>在庫元</th><td>${locationName(plan.sourceLocationName)}</td><td>${locationName(plan.nextSourceLocationName)}</td></tr>
          <tr><th>補充元</th><td>${locationName(plan.replenishmentLocationName)}</td><td>${locationName(plan.nextReplenishmentLocationName)}</td></tr>
        </tbody></table></section>
      <p class="inventory-transfer-request-guide">同じグループの他の商品は変更しません。「取消」を押すと変更内容は保存されません。</p>
    </div><footer class="inventory-transfer-request-actions"><span></span><div class="inventory-relocation-confirm-actions">
      <button class="button secondary" type="button" data-regroup-cancel>取消</button>
      <button class="button inventory-relocation-submit" type="button" data-regroup-confirm>変更して移動</button>
    </div></footer></div>`;
}

export function inventoryRelocationReturnDialogHtml(records: readonly InventoryRelocationRecord[]): string {
  const groups = new Map<string, InventoryRelocationRecord[]>();
  for (const record of records) {
    const group = groups.get(record.groupName) ?? [];
    group.push(record);
    groups.set(record.groupName, group);
  }
  return `<form class="inventory-transfer-request-form" data-relocation-return-form>
    <header class="inventory-transfer-request-head"><div><p>INVENTORY TRANSFER</p><h2 id="inventory-relocation-return-title">補充依頼管理に戻す商品を選択</h2></div><button class="dialog-close" type="button" data-relocation-return-close aria-label="閉じる">×</button></header>
    <div class="inventory-transfer-request-body">
      <p class="inventory-transfer-request-guide">戻す商品にチェックを入れてください。選択した商品のみ「依頼中」で補充依頼管理に戻します。数量・問題メモなどは保持し、選択していない商品は元のグループに残します。</p>
      <div class="inventory-return-passcode" data-return-passcode-area hidden><label>管理者パスコード<input type="password" inputmode="numeric" autocomplete="off" maxlength="128" data-return-passcode></label><p>Shopifyの下書き全体を削除します。残りの商品は同じPSTに未登録で残します。移動中以降は戻せません。</p></div>
      ${[...groups].map(([name, items]) => `<section class="inventory-relocation-confirm-section inventory-relocation-return-group"><h3>${escapeHtml(name)} ／ ${escapeHtml(formatTransferDateOnly(items[0]!.groupDate))}</h3>
        <table><thead><tr><th>選択</th><th>商品名</th><th>SKU</th><th>数量</th></tr></thead><tbody>
        ${items.map((record) => `<tr><td><input type="checkbox" data-relocation-return-select="${escapeHtml(record.id)}" aria-label="${escapeHtml(name)} ${escapeHtml(record.sku || record.productTitle)}を戻す" ${record.shopifySyncOperationId && !record.shopifySyncOperationId.startsWith('ACTION-') ? 'disabled title="Shopify登録確認中の商品は戻せません"' : ''}></td><td class="inventory-replenishment-product-cell">${inventoryProductHtml(record)}${record.shopifyTransferId ? '<small>すでに、移管登録済みのグループです、元に戻すには管理者パスコードが必要です。</small>' : ''}</td><td><code>${escapeHtml(record.sku || "—")}</code></td><td>${record.quantity}</td></tr>`).join("")}
        </tbody></table></section>`).join("") || '<p class="inventory-transfer-request-guide">戻せる商品はありません。</p>'}
    </div>
    <footer class="inventory-transfer-request-actions"><span data-relocation-return-count>0件を選択</span><div class="inventory-relocation-confirm-actions"><button class="button secondary" type="button" data-relocation-return-close>取消</button><button class="button inventory-relocation-submit" type="submit" disabled>補充依頼管理に戻す</button></div></footer>
  </form>`;
}

function inventoryProductHtml(record: Pick<InventoryReplenishmentRecord, "productTitle" | "variantTitle" | "productImage">): string {
  const image = record.productImage?.url
    ? `<img src="${escapeHtml(record.productImage.url)}" alt="${escapeHtml(record.productImage.altText || record.productTitle)}" loading="lazy" decoding="async">`
    : '<span class="inventory-replenishment-product-placeholder" aria-hidden="true">NO IMAGE</span>';
  return `<div class="inventory-replenishment-product">${image}<span><strong>${escapeHtml(record.productTitle || "—")}</strong><small>${escapeHtml(record.variantTitle || "—")}</small></span></div>`;
}

function inventoryRelocationIssueHtml(record: InventoryRelocationRecord, disabled: boolean): string {
  return `<div class="inventory-replenishment-update-actions" aria-label="問題発生">
    <button class="inventory-replenishment-update-button issue${record.replenishmentIssue ? " has-note" : ""}" type="button" data-action="inventory-replenishment-issue" data-inventory-replenishment-id="${escapeHtml(record.id)}" aria-label="${escapeHtml(record.sku || record.productTitle)}の問題メモを${record.replenishmentIssue ? "編集" : "記録"}" title="${record.replenishmentIssue ? "問題メモを編集" : "問題内容を記録"}"${disabled ? " disabled" : ""}>問題あり</button>
  </div>`;
}

function inventoryRelocationTableHtml(
  records: readonly InventoryRelocationRecord[],
  locations: readonly InventorySourceLocation[],
  selectedGroups: ReadonlySet<string>,
  savingIds: ReadonlySet<string>,
  locationsError: string,
): string {
  const groups = new Map<string, InventoryRelocationRecord[]>();
  for (const record of records) {
    const group = groups.get(record.groupName) ?? [];
    group.push(record);
    groups.set(record.groupName, group);
  }
  const defaultLocationId = defaultInventorySourceLocation(locations)?.id ?? "";
  const locationDropdown = (record: InventoryRelocationRecord, field: "source" | "replenishment") => {
    const isSource = field === "source";
    const label = isSource ? "在庫元" : "補充元";
    const selectedId = isSource ? record.sourceLocationId ?? defaultLocationId
      : record.replenishmentLocationId ?? defaultInventoryReplenishmentLocation(locations, record.requestedByEmail)?.id ?? "";
    const savedName = isSource ? record.sourceLocationName : record.replenishmentLocationName;
    const unavailable = selectedId && !locations.some((location) => location.id === selectedId);
    return `<select class="inventory-relocation-source" data-relocation-${field}="${escapeHtml(record.id)}" data-relocation-location-field="${field}" aria-label="${escapeHtml(record.sku)}の${label}" ${savingIds.size > 0 || locations.length === 0 || record.shopifyRegisteredAt || record.shopifySyncOperationId ? "disabled" : ""}>
      <option value="" disabled ${!selectedId ? "selected" : ""}>${locationsError ? "取得できませんでした" : "選択してください"}</option>
      ${unavailable ? `<option value="${escapeHtml(selectedId)}" selected disabled>${escapeHtml(savedName || "保存済みロケーション")}（現在利用不可）</option>` : ""}
      ${locations.map((location) => `<option value="${escapeHtml(location.id)}" ${location.id === selectedId ? "selected" : ""}>${escapeHtml(location.name)}</option>`).join("")}
    </select>${savingIds.has(record.id) ? '<small role="status">保存中…</small>' : ""}`;
  };
  const bodies = [...groups.values()].map((group) => `<tbody class="inventory-relocation-group${group.every((record) => record.processingStatus === "移管登録済") ? " is-registered" : ""}${selectedGroups.has(group[0]!.groupName) ? " is-selected" : ""}">${group.map((record, index) => `<tr>
    ${index === 0 ? `<td class="inventory-relocation-selection" rowspan="${group.length}"><input type="checkbox" data-relocation-group-select="${escapeHtml(record.groupName)}" aria-label="${escapeHtml(record.groupName)}を選択" ${selectedGroups.has(record.groupName) ? "checked" : ""}></td><td class="inventory-relocation-group-name" ${record.shopifyTransferId ? `data-relocation-detail="${escapeHtml(record.groupName)}" role="button" tabindex="0" aria-label="${escapeHtml(record.groupName)}のShopify移管詳細"` : ""} rowspan="${group.length}"><strong>${escapeHtml(record.groupName)}</strong><br><small>${escapeHtml(formatTransferDateOnly(record.groupDate))}</small>${record.shopifyTransferId ? `<br><small class="inventory-shopify-registered inventory-shopify-draft-badge">Shopify ${escapeHtml(record.shopifyTransferName || "登録済み")}</small>` : ""}${record.shopifySyncOperationId ? `<br><small>登録結果の再確認が必要</small>` : ""}</td>` : ""}
    <td>${escapeHtml(formatTransferDateOnly(record.requestedAt))}</td>
    <td>${locationDropdown(record, "source")}</td>
    <td>${escapeHtml(inventoryReplenishmentRequesterLabel(record.requestedByEmail))}</td>
    <td>${locationDropdown(record, "replenishment")}</td>
    <td>${inventoryRelocationIssueHtml(record, savingIds.size > 0)}</td>
    <td class="inventory-replenishment-product-cell">${inventoryProductHtml(record)}</td>
    <td><span class="inventory-transfer-sku"><code>${escapeHtml(record.sku || "—")}</code></span></td>
    <td class="inventory-transfer-quantity">${record.quantity}</td>
    <td>${escapeHtml(formatTransferDateOnly(record.desiredShippingDate))}</td>
    <td><span class="inventory-replenishment-status ${record.processingStatus === "移管登録済" ? "registered" : "receiving"}">${escapeHtml(record.processingStatus)}</span>${!record.shopifyRegisteredAt && record.shopifyTransferId ? `<br><span class="inventory-shopify-registered is-unsynced">Shopify未登録</span>` : ""}</td>
  </tr>`).join("")}</tbody>`).join("");
  return `${locationsError ? `<p class="inventory-relocation-location-error" role="alert">${escapeHtml(locationsError)}「再読込」で再取得できます。</p>` : ""}<div class="inventory-transfer-table-wrap"><table class="inventory-transfer-table inventory-relocation-table">
    <colgroup><col class="col-selection"><col class="col-group"><col class="col-date"><col class="col-source"><col class="col-requester"><col class="col-source"><col class="col-issue"><col class="col-product"><col class="col-sku"><col class="col-quantity"><col class="col-date"><col class="col-status"></colgroup>
    <thead><tr><th aria-label="グループ選択"></th><th>グループ名</th><th>依頼日</th><th>在庫元</th><th>依頼元</th><th>補充元</th><th>問題発生</th><th>商品名</th><th>SKU</th><th>数量</th><th>発送希望日</th><th>処理状況</th></tr></thead>
    ${bodies || '<tbody><tr><td class="inventory-transfer-empty" colspan="12">在庫移管の商品はありません。</td></tr></tbody>'}
  </table></div>`;
}

export interface InventoryTransferDialogItem {
  id: string;
  sku: string;
  title: string;
  variantTitle: string | null;
  quantity: number;
  currentQuantity: number;
  properties: Array<{ name: string; value: string }>;
  inventoryLocations?: Array<{ id: string; name: string; available: number }>;
}

export interface InventoryTransferDialogOrder {
  id: string;
  name: string;
  customerName: string;
  items: InventoryTransferDialogItem[];
}

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function displayOrderNumber(value: string): string {
  return value.replace(/^#/, "");
}

export function formatTransferDate(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatTransferDateOnly(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function inventoryReplenishmentRequesterLabel(email: string): string {
  const normalized = email.trim().toLowerCase();
  if (normalized === "app-admin@tweed.co.jp") return "天野";
  if (normalized === "jiyugaoka_tokyo@tweed.co.jp") return "自由が丘・スタッフ";
  return normalized || "—";
}

function inventoryReplenishmentStatusTone(status: InventoryReplenishmentRecord["processingStatus"]): string {
  if (status === "依頼中") return "requested";
  if (status === "受付・在庫確認中") return "receiving";
  if (status === "保留・再依頼要") return "rerequest";
  return "pending";
}

export function inventoryTransferWorkspaceHtml(
  data: InventoryTransferData | null,
  loading: boolean,
  error: string,
  view: InventoryTransferView = "active",
  selectedReplenishmentIds: ReadonlySet<string> = new Set<string>(),
  selectedRelocationGroups: ReadonlySet<string> = new Set<string>(),
  relocationSavingIds: ReadonlySet<string> = new Set<string>(),
): string {
  const records = data?.records ?? [];
  const counts = inventoryTransferViewCounts(records);
  const replenishmentRecords = data?.replenishmentRecords ?? [];
  const rawReplenishmentCount = Number(data?.replenishmentCount ?? replenishmentRecords.length);
  const replenishmentCount = Number.isFinite(rawReplenishmentCount)
    ? Math.max(0, Math.trunc(rawReplenishmentCount))
    : 0;
  const replenishmentView = view === "replenishment";
  const relocationView = view === "relocation";
  const relocationRecords = data?.relocationRecords ?? [];
  const selectedRelocationCount = selectedInventoryRelocationRecords(relocationRecords, selectedRelocationGroups).length;
  const activeView = view === "active";
  const historyView = view === "history";
  const selectedReplenishmentCount = replenishmentRecords.reduce(
    (count, record) => count + (selectedReplenishmentIds.has(record.id) ? 1 : 0),
    0,
  );
  let body = "";
  if (loading) {
    body = `<div class="inventory-transfer-loading" role="status"><span class="workspace-loading-spinner" aria-hidden="true"></span><strong>在庫転送依頼を読み込んでいます…</strong></div>`;
  } else if (error) {
    body = `<div class="inventory-transfer-error" role="alert"><strong>在庫転送依頼を読み込めませんでした</strong><p>${escapeHtml(error)}</p><button class="button secondary" type="button" data-action="inventory-transfer-refresh">再試行</button></div>`;
  } else if (replenishmentView) {
    const allSelected = replenishmentRecords.length > 0 && selectedReplenishmentCount === replenishmentRecords.length;
    const partlySelected = selectedReplenishmentCount > 0 && !allSelected;
    const rows = replenishmentRecords.map((record) => {
      const selected = selectedReplenishmentIds.has(record.id);
      return `<tr${selected ? ' class="inventory-replenishment-selected-row"' : ""}>
        <td class="inventory-replenishment-selection"><input type="checkbox" data-inventory-replenishment-select="${escapeHtml(record.id)}" aria-label="${escapeHtml(record.sku || record.productTitle)}を選択" ${selected ? "checked" : ""}></td>
        <td class="inventory-replenishment-requested-date">${escapeHtml(formatTransferDateOnly(record.requestedAt))}</td>
        <td><span class="inventory-replenishment-requester">${escapeHtml(inventoryReplenishmentRequesterLabel(record.requestedByEmail))}</span></td>
        <td><span class="inventory-replenishment-request-type">${escapeHtml(record.requestType || "—")}</span></td>
        <td class="inventory-replenishment-product-cell">${inventoryProductHtml(record)}</td>
        <td class="inventory-replenishment-quantity"><strong>${record.quantity}</strong></td>
        <td class="inventory-replenishment-desired-date">${escapeHtml(formatTransferDateOnly(record.desiredShippingDate || ""))}</td>
        <td class="inventory-replenishment-visit-at">${escapeHtml(formatTransferDate(record.visitAt || ""))}</td>
        <td class="inventory-replenishment-customer">${escapeHtml(record.customerName || "—")}</td>
        <td><span class="inventory-replenishment-status ${inventoryReplenishmentStatusTone(record.processingStatus)}">${escapeHtml(record.processingStatus)}</span></td>
        <td class="inventory-replenishment-remarks">${escapeHtml(record.remarks || "—")}</td>
      </tr>`;
    }).join("");
    body = `<div class="inventory-transfer-table-wrap"><table class="inventory-transfer-table inventory-replenishment-table">
      <colgroup><col class="col-selection"><col class="col-requested"><col class="col-requester"><col class="col-request-type"><col class="col-product"><col class="col-quantity"><col class="col-desired-date"><col class="col-visit-at"><col class="col-customer"><col class="col-status"><col class="col-remarks"></colgroup>
      <thead><tr><th class="inventory-replenishment-selection"><input type="checkbox" data-inventory-replenishment-select-all aria-label="表示中の補充依頼をすべて選択" aria-checked="${partlySelected ? "mixed" : allSelected}" ${allSelected ? "checked" : ""} ${replenishmentRecords.length === 0 ? "disabled" : ""}></th><th>依頼日</th><th>依頼元</th><th>依頼種別</th><th>商品名</th><th>数量</th><th>発送希望日</th><th>来店日時</th><th>顧客名</th><th>処理状況</th><th>備考</th></tr></thead>
      <tbody>${rows || '<tr><td class="inventory-transfer-empty" colspan="11">補充依頼はまだありません。</td></tr>'}</tbody>
    </table></div>`;
  } else if (relocationView) {
    body = inventoryRelocationTableHtml(relocationRecords, data?.relocationLocations ?? [], selectedRelocationGroups, relocationSavingIds, data?.relocationLocationsError ?? "");
  } else {
    const visibleRecords = inventoryTransferViewRecords(records, view);
    const rows = visibleRecords.map((record) => {
      const statusTone = record.processingStatus === "出荷済み"
        ? "shipped"
        : record.processingStatus === "確認済み"
          ? "confirmed"
          : "pending";
      return `<tr${record.processingStatus === "出荷済み" ? ' class="inventory-transfer-history-row"' : ""}>
      <td><div class="inventory-transfer-order"><strong>${escapeHtml(displayOrderNumber(record.orderName))}</strong><span>${escapeHtml(record.customerName || "ゲスト注文")} 様</span><small>Order ID: ${escapeHtml(record.legacyId)}</small></div></td>
      <td><span class="inventory-transfer-sku"><code>${escapeHtml(record.sku)}</code></span></td>
      <td class="inventory-transfer-quantity">${record.quantity}</td>
      <td>${escapeHtml(record.size || "—")}</td>
      <td>${escapeHtml(record.specificName || "—")}</td>
      <td><span class="inventory-transfer-route"><b>${escapeHtml(record.requestLocation)}</b><i aria-hidden="true">🔙</i><b>${escapeHtml(record.receivingLocation)}</b></span></td>
      <td>${escapeHtml(formatTransferDate(record.requestedAt))}</td>
      <td><span class="inventory-transfer-confirmation ${statusTone}">${escapeHtml(record.processingStatus)}</span></td>
      <td>${escapeHtml(formatTransferDate(record.processingAt))}</td>
    </tr>`;
    }).join("");
    body = `<div class="inventory-transfer-table-wrap"><table class="inventory-transfer-table">
      <colgroup><col class="col-order"><col class="col-sku"><col class="col-quantity"><col class="col-size"><col class="col-name"><col class="col-route"><col class="col-requested"><col class="col-confirmation"><col class="col-confirmed-at"></colgroup>
      <thead><tr><th>注文・注文者</th><th>対象SKU</th><th class="inventory-transfer-quantity">数量</th><th>サイズ</th><th>特定名</th><th>依頼ロケーション</th><th>転送依頼日</th><th>処理状況</th><th>処理日</th></tr></thead>
      <tbody>${rows || `<tr><td class="inventory-transfer-empty" colspan="9">${historyView ? "転送済みの依頼はまだありません。" : "在庫転送依頼はまだありません。"}</td></tr>`}</tbody>
    </table></div>`;
  }

  return `<main class="inventory-transfer-main${replenishmentView ? " has-sticky-replenishment" : ""}"><section class="inventory-transfer-panel">
    ${replenishmentView ? '<div class="inventory-transfer-sticky-head">' : ""}
    <header class="inventory-transfer-head"><div><p>INVENTORY TRANSFER</p><h1>在庫転送管理</h1></div><div class="inventory-transfer-head-actions">${relocationView ? `<button class="button inventory-relocation-return" type="button" data-action="inventory-relocation-return" ${loading || relocationSavingIds.size > 0 || selectedRelocationCount === 0 ? "disabled" : ""}>元に戻す</button><button class="button inventory-replenishment-receipt-print" type="button" data-action="inventory-replenishment-receipt-print" ${loading || relocationSavingIds.size > 0 || selectedRelocationCount === 0 ? "disabled" : ""}>受付・印刷</button><button class="button inventory-relocation-register" type="button" data-action="inventory-relocation-register" ${loading || relocationSavingIds.size > 0 || selectedRelocationCount === 0 ? "disabled" : ""}>移管登録</button>` : ""}${replenishmentView ? `<button class="button inventory-relocation-submit" type="button" data-action="inventory-replenishment-relocate" ${loading || selectedReplenishmentCount === 0 ? "disabled" : ""}>移管する</button>` : ""}<span><strong>${replenishmentView ? replenishmentCount : relocationView ? relocationRecords.length : historyView ? counts.history : counts.active}</strong> 件</span><button class="button secondary" type="button" data-action="inventory-transfer-refresh" ${loading ? "disabled" : ""}><span class="button-icon${loading ? " is-spinning" : ""}">↻</span>${loading ? "再読込中…" : "再読込"}</button></div></header>
    <nav class="inventory-transfer-subtabs" role="tablist" aria-label="在庫転送管理表示">
      <button class="inventory-transfer-subtab${activeView ? " is-active" : ""}" type="button" role="tab" aria-selected="${activeView}" data-action="inventory-transfer-active" ${loading ? "disabled" : ""}>転送依頼中 <span>${counts.active}</span></button>
      <button class="inventory-transfer-subtab history${historyView ? " is-active" : ""}" type="button" role="tab" aria-selected="${historyView}" data-action="inventory-transfer-history" ${loading ? "disabled" : ""}>転送済み・履歴 <span>${counts.history}</span></button>
      <button class="inventory-transfer-subtab replenishment${replenishmentView ? " is-active" : ""}" type="button" role="tab" aria-selected="${replenishmentView}" data-action="inventory-transfer-replenishment" ${loading ? "disabled" : ""}>補充依頼管理 <span class="inventory-transfer-count${replenishmentCount > 0 ? " has-items" : ""}">${replenishmentCount}</span></button>
      <button class="inventory-transfer-subtab${relocationView ? " is-active" : ""}" type="button" role="tab" aria-selected="${relocationView}" data-action="inventory-transfer-relocation" ${loading ? "disabled" : ""}>在庫移管 <span class="inventory-transfer-count${relocationRecords.length > 0 ? " has-items" : ""}">${relocationRecords.length}</span></button>
    </nav>
    ${replenishmentView ? "</div>" : ""}
    ${body}
  </section></main>`;
}

export function inventoryTransferRequestDialogHtml(
  orders: readonly InventoryTransferDialogOrder[],
): string {
  const items = orders.flatMap((order) => order.items.filter((item) => item.sku.trim()));
  const receivingLocations = inventoryTransferReceivingLocations(items);
  const groups = orders.map((order) => {
    const itemOptions = order.items.filter((item) => item.sku.trim()).map((item) => {
      const maxQuantity = inventoryTransferMaxQuantity(item);
      const disabled = maxQuantity === 0;
      return `<label class="inventory-transfer-item-option${disabled ? " is-disabled" : ""}">
        <input type="checkbox" data-transfer-line-item data-order-id="${escapeHtml(order.id)}" data-line-item-id="${escapeHtml(item.id)}" data-max-quantity="${maxQuantity}" ${disabled ? "disabled" : ""}>
        <span><code>${escapeHtml(item.sku.trim())}</code><strong>${escapeHtml(item.title.trim() || "—")}</strong><small>サイズ ${escapeHtml(orderListItemSize(item) || "—")} · 依頼可能 ${maxQuantity}${disabled ? "（キャンセル済み）" : ""}</small></span>
      </label>`;
    }).join("");
    return `<section class="inventory-transfer-order-group"><header><strong>${escapeHtml(displayOrderNumber(order.name))}</strong><span>${escapeHtml(order.customerName || "ゲスト注文")} 様</span></header><div class="inventory-transfer-item-options">${itemOptions || '<p class="inventory-transfer-request-empty">SKUが登録されたline-itemはありません。</p>'}</div></section>`;
  }).join("");

  return `<form class="inventory-transfer-request-form">
    <header class="inventory-transfer-request-head"><div><p>INVENTORY TRANSFER REQUEST</p><h2>在庫転送依頼</h2></div><button class="dialog-close" type="button" data-inventory-transfer-close aria-label="閉じる">×</button></header>
    <div class="inventory-transfer-request-body"><p class="inventory-transfer-request-guide">選択した注文のline-itemから、在庫転送が必要なSKUバッジを選択してください。</p>${groups || '<p class="inventory-transfer-request-empty">対象注文を確認できませんでした。</p>'}
      <div class="inventory-transfer-route-form"><label>依頼側location<input value="大阪" disabled></label><span class="inventory-transfer-route-arrow" aria-hidden="true">🔙</span><label>依頼を受ける側location<select name="receivingLocation">${receivingLocations.map((location) => `<option value="${escapeHtml(location)}">${escapeHtml(location)}</option>`).join("")}</select></label><label>数量<select name="quantity" disabled><option value="1">1</option></select></label></div>
    </div>
    <footer class="inventory-transfer-request-actions"><span class="inventory-transfer-selection-summary"><strong data-transfer-selection-count>0</strong> SKU選択中</span><button class="button inventory-transfer-submit" type="submit" disabled>在庫転送依頼する</button></footer>
  </form>`;
}
